import React, { useState, useEffect } from 'react';
import './AccessibilityMenu.css';

const AccessibilityMenu = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [contrast, setContrast] = useState(localStorage.getItem('accessibility-contrast') || 'normal');
    const [fontSize, setFontSize] = useState(parseInt(localStorage.getItem('accessibility-font-size')) || 100);
    const [darkMode, setDarkMode] = useState(localStorage.getItem('accessibility-dark-mode') === 'true');
    const [lineSpacing, setLineSpacing] = useState(localStorage.getItem('accessibility-line-spacing') || 'normal');
    const [letterSpacing, setLetterSpacing] = useState(localStorage.getItem('accessibility-letter-spacing') || 'normal');
    const [textAlign, setTextAlign] = useState(localStorage.getItem('accessibility-text-align') || 'initial');
    const [saturation, setSaturation] = useState(localStorage.getItem('accessibility-saturation') || 'normal');

    useEffect(() => {
        document.body.classList.remove('high-contrast');
        if (contrast === 'high') {
            document.body.classList.add('high-contrast');
        }
        localStorage.setItem('accessibility-contrast', contrast);
    }, [contrast]);

    useEffect(() => {
        document.documentElement.style.fontSize = `${fontSize}%`;
        localStorage.setItem('accessibility-font-size', fontSize);
    }, [fontSize]);

    useEffect(() => {
        if (darkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
        localStorage.setItem('accessibility-dark-mode', darkMode);
    }, [darkMode]);

    useEffect(() => {
        document.body.classList.remove('spacing-wide', 'spacing-wider');
        if (lineSpacing === 'wide') document.body.classList.add('spacing-wide');
        if (lineSpacing === 'wider') document.body.classList.add('spacing-wider');
        localStorage.setItem('accessibility-line-spacing', lineSpacing);
    }, [lineSpacing]);

    useEffect(() => {
        document.body.classList.remove('letter-spacing-wide', 'letter-spacing-wider');
        if (letterSpacing === 'wide') document.body.classList.add('letter-spacing-wide');
        if (letterSpacing === 'wider') document.body.classList.add('letter-spacing-wider');
        localStorage.setItem('accessibility-letter-spacing', letterSpacing);
    }, [letterSpacing]);

    useEffect(() => {
        const applyTextAlign = (element) => {
            if (element.classList.contains('accessibility-container') || element.closest('.accessibility-container')) {
                return;
            }
            element.style.setProperty('text-align', textAlign, 'important');
            Array.from(element.children).forEach(applyTextAlign);
        };
        
        document.body.style.setProperty('text-align', textAlign, 'important');
        // Apply to all direct descendants of body to overcome internal specificity
        Array.from(document.body.children).forEach(applyTextAlign);
        
        localStorage.setItem('accessibility-text-align', textAlign);
    }, [textAlign]);

    useEffect(() => {
        document.body.classList.remove('sat-low', 'sat-high', 'sat-grayscale');
        if (saturation === 'low') document.body.classList.add('sat-low');
        if (saturation === 'high') document.body.classList.add('sat-high');
        if (saturation === 'grayscale') document.body.classList.add('sat-grayscale');
        localStorage.setItem('accessibility-saturation', saturation);
    }, [saturation]);

    const toggleMenu = () => setIsOpen(!isOpen);

    const toggleContrast = () => {
        setContrast(prev => prev === 'normal' ? 'high' : 'normal');
    };

    const toggleDarkMode = () => setDarkMode(!darkMode);

    const increaseFontSize = () => {
        setFontSize(prev => Math.min(prev + 10, 150));
    };

    const decreaseFontSize = () => {
        setFontSize(prev => Math.max(prev - 10, 80));
    };

    const cycleLineSpacing = () => {
        setLineSpacing(prev => {
            if (prev === 'normal') return 'wide';
            if (prev === 'wide') return 'wider';
            return 'normal';
        });
    };

    const cycleLetterSpacing = () => {
        setLetterSpacing(prev => {
            if (prev === 'normal') return 'wide';
            if (prev === 'wide') return 'wider';
            return 'normal';
        });
    };

    const cycleTextAlign = () => {
        setTextAlign(prev => {
            if (prev === 'initial') return 'left';
            if (prev === 'left') return 'center';
            if (prev === 'center') return 'right';
            if (prev === 'right') return 'justify';
            return 'initial';
        });
    };

    const cycleSaturation = () => {
        setSaturation(prev => {
            if (prev === 'normal') return 'low';
            if (prev === 'low') return 'high';
            if (prev === 'high') return 'grayscale';
            return 'normal';
        });
    };

    const reset = () => {
        setContrast('normal');
        setFontSize(100);
        setDarkMode(false);
        setLineSpacing('normal');
        setLetterSpacing('normal');
        setTextAlign('initial');
        setSaturation('normal');
        
        // Explicitly clear inline text-align that might have been applied by the useEffect
        const clearTextAlign = (element) => {
            if (element.classList.contains('accessibility-container') || element.closest('.accessibility-container')) {
                return;
            }
            element.style.textAlign = '';
            Array.from(element.children).forEach(clearTextAlign);
        };
        document.body.style.textAlign = '';
        Array.from(document.body.children).forEach(clearTextAlign);
    };

    return (
        <div className={`accessibility-container ${isOpen ? 'open' : ''}`} style={{ fontSize: '16px' }}>
            <button 
                className="accessibility-toggle" 
                onClick={toggleMenu}
                aria-label="Meniu Accesibilitate"
                title="Meniu Accesibilitate"
            >
                <i className="fas fa-universal-access"></i>
            </button>
            {isOpen && (
                <div className="accessibility-menu">
                    <h5>Accesibilitate</h5>
                    <div className="accessibility-grid">
                        <button onClick={toggleContrast} title="Contrast">
                            <i className="fas fa-adjust"></i>
                            <span>{contrast === 'high' ? 'Normal' : 'Ridicat'}</span>
                        </button>
                        <button onClick={toggleDarkMode} title="Dark Mode">
                            <i className="fas fa-moon"></i>
                            <span>{darkMode ? 'Light' : 'Dark'}</span>
                        </button>
                        <button onClick={increaseFontSize} title="Mărește Textul">
                            <i className="fas fa-plus"></i>
                            <span>Text+</span>
                        </button>
                        <button onClick={decreaseFontSize} title="Micșorează Textul">
                            <i className="fas fa-minus"></i>
                            <span>Text-</span>
                        </button>
                        <button onClick={cycleLineSpacing} title="Spațiere Rânduri">
                            <i className="fas fa-arrows-alt-v"></i>
                            <span>Rânduri</span>
                        </button>
                        <button onClick={cycleLetterSpacing} title="Spațiere Litere">
                            <i className="fas fa-arrows-alt-h"></i>
                            <span>Litere</span>
                        </button>
                        <button onClick={cycleTextAlign} title="Aliniere Text">
                            <i className="fas fa-align-left"></i>
                            <span>Aliniere</span>
                        </button>
                        <button onClick={cycleSaturation} title="Saturație">
                            <i className="fas fa-palette"></i>
                            <span>Saturație</span>
                        </button>
                        <button onClick={reset} className="reset-btn" title="Resetează">
                            <i className="fas fa-undo"></i>
                            <span>Reset</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccessibilityMenu;
